// src/components/core/ListProvider.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  CheckOutlined,
  ContainerOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FilterOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  FontSizeOutlined,
  MoreOutlined,
  ReloadOutlined,
  RestOutlined,
  SearchOutlined,
  SettingOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Button, Col, Dropdown, Input, Row, Space, Switch, theme, Tooltip, Typography } from 'antd';

const { Text } = Typography;
import { NumericFormat } from 'react-number-format';
import appService from 'services/appService';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useModuleStore, getStorageModuleItem, setStorageModuleItem } from 'stores/module';
import { isMobileView } from 'utils/helpers';
import translateX from 'utils/translateX';
import useApi from 'hooks/useApi';
import useApiInitializer from 'hooks/useApiInitializer';
import useZustand from 'hooks/useZustand';
import { useModuleContext } from './ModuleProvider';
import { useDocumentContext } from './DocumentProvider';
import ListActionsFloatButton from '../JarvislyModule/ListActionsFloatButton';
import SimpleEntityProfileTag from '../SimpleComponents/SimpleEntityProfileTag';
import DocumentInfoIcons from '../JarvislyComponents/DocumentInfoIcons';
import dayjs from 'dayjs';
import { PiLifebuoy } from 'react-icons/pi';

// @dnd-kit imports for column drag and drop
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import BackToHomeLink from 'components/core/BackToHomeLink';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const QUERY_PARAM_KEY = 'q';
const TEXT_SEARCH_FIELDS = [];
const VISUAL_FILTER_FIELDS = new Set();

const DEFAULT_QUERY = {
  pagination: { page: 1, size: 10 },
  sort: [], // Array format: [{ field: 'name', order: 'asc' }, ...]
  search: {},
  textSearch: '',
  filter: {},
  drawer: { folder: 'inbox' },
};

// *******************************************************************************
// DRAG AND DROP - Context and Components
// *******************************************************************************
const DragIndexContext = createContext({ active: -1, over: -1 });
const TableEditContext = createContext({
  isEditing: false,
  hiddenColumns: [],
  onToggleColumnVisibility: null,
  hoveredColumnId: null,
  setHoveredColumnId: null,
});

// Opacity for hidden columns in edit mode (ghost effect)
const HIDDEN_COLUMN_OPACITY = 0.35;

const VISIBILITY_BUTTON_STYLE = {
  position: 'absolute',
  top: 34,
  left: '50%',
  transform: 'translateX(-50%)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
  zIndex: 9999,
};

const TABLE_FEATURES_ROW_STYLE = {
  height: 48,
  margin: '0 4px 4px',
};

/**
 * Helper to apply drag active styles to cells
 */
const getDragActiveStyle = (dragState, id, isEditing) => {
  if (!isEditing) return {};

  const { active, over } = dragState;
  let style = {};

  if (active && active === id) {
    style = { backgroundColor: 'rgba(0, 0, 0, 0.05)', opacity: 0.5 };
  } else if (over && id === over && active !== over) {
    style = { borderInlineStart: '2px dashed var(--ant-color-primary)' };
  }

  return style;
};

/**
 * Draggable table header cell component with visibility toggle
 */
const TableHeaderCell = props => {
  const { token } = theme.useToken();
  const dragState = useContext(DragIndexContext);
  const {
    isEditing,
    hiddenColumns,
    onToggleColumnVisibility,
    hoveredColumnId,
    setHoveredColumnId,
  } = useContext(TableEditContext);

  // Check if this is a fixed column (Ant Design uses sticky positioning and fix classes)
  const isFixedColumn =
    props.style?.position === 'sticky' || props.className?.includes('ant-table-cell-fix');

  // Use a stable placeholder id for columns without id (checkbox, etc.)
  const sortableId = props.id || '__non-draggable__';
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: sortableId,
    // Disable drag for: columns without id, fixed columns, or when not editing
    disabled: !props.id || !isEditing || isFixedColumn,
  });

  // Check if this column is hidden
  const isColumnHidden = props.id && hiddenColumns?.includes(props.id);

  // Check if this column is being hovered (header or any body cell)
  const isColumnHovered = props.id && hoveredColumnId === props.id;

  // Skip drag functionality if no id (checkbox column, etc.)
  if (!props.id) {
    // Selection column: add hover/eye behavior in edit mode
    if (isEditing && props.className?.includes('ant-table-selection-column')) {
      const isHovered = hoveredColumnId === '__selection__';
      const isHidden = hiddenColumns?.includes('__selection__');
      const EyeIcon = isHidden ? EyeInvisibleOutlined : EyeOutlined;
      const selectionHeaderCellStyle = {
        ...props.style,
        position: 'relative',
        overflow: 'visible',
        ...(isHidden ? { opacity: HIDDEN_COLUMN_OPACITY } : {}),
      };
      return (
        <th
          {...props}
          style={selectionHeaderCellStyle}
          onMouseEnter={() => setHoveredColumnId?.('__selection__')}
          onMouseLeave={() => setHoveredColumnId?.(null)}
        >
          {props.children}
          {isHovered && (
            <Tooltip title={isHidden ? translateX('show_column') : translateX('hide_column')}>
              <Button
                shape="circle"
                icon={
                  <EyeIcon style={{ color: isHidden ? token.colorError : token.colorPrimary }} />
                }
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleColumnVisibility?.('__selection__');
                }}
                onMouseEnter={() => setHoveredColumnId?.('__selection__')}
                onMouseLeave={() => setHoveredColumnId?.(null)}
                style={VISIBILITY_BUTTON_STYLE}
              />
            </Tooltip>
          )}
        </th>
      );
    }
    return <th {...props} />;
  }

  // For fixed columns, render without any drag modifications to preserve sticky behavior
  if (isFixedColumn) {
    return <th {...props} />;
  }

  const style = {
    ...props.style,
    cursor: isEditing ? 'move' : undefined,
    position: 'relative',
    overflow: isEditing ? 'visible' : undefined, // Allow button to overflow when editing
    ...(isDragging ? { zIndex: 9999, userSelect: 'none' } : {}),
    // When editing and column is hovered, ensure header is above body cells for button click
    ...(isEditing && isColumnHovered ? { zIndex: 100 } : {}),
    ...getDragActiveStyle(dragState, props.id, isEditing),
    // Apply ghost effect for hidden columns in edit mode
    ...(isEditing && isColumnHidden ? { opacity: HIDDEN_COLUMN_OPACITY } : {}),
  };

  const handleToggleVisibility = e => {
    e.stopPropagation();
    e.preventDefault();
    onToggleColumnVisibility?.(props.id);
  };

  const EyeIcon = isColumnHidden ? EyeInvisibleOutlined : EyeOutlined;
  const tooltipText = isColumnHidden ? translateX('show_column') : translateX('hide_column');

  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...(isEditing ? { ...attributes, ...listeners } : {})}
      onMouseEnter={() => setHoveredColumnId?.(props.id)}
      onMouseLeave={() => setHoveredColumnId?.(null)}
    >
      {props.children}
      {isEditing && isColumnHovered && (
        <Tooltip title={tooltipText}>
          <Button
            shape="circle"
            icon={
              <EyeIcon style={{ color: isColumnHidden ? token.colorError : token.colorPrimary }} />
            }
            onClick={handleToggleVisibility}
            onMouseEnter={() => setHoveredColumnId?.(props.id)}
            onMouseLeave={() => setHoveredColumnId?.(null)}
            style={VISIBILITY_BUTTON_STYLE}
          />
        </Tooltip>
      )}
    </th>
  );
};

/**
 * Table body cell with drag visual feedback and hidden column styling
 */
const TableBodyCell = props => {
  const dragState = useContext(DragIndexContext);
  const { isEditing, hiddenColumns, setHoveredColumnId } = useContext(TableEditContext);

  // Check if this column is hidden
  const isColumnHidden = props.id && hiddenColumns?.includes(props.id);

  // Skip drag styling if no id (checkbox column, etc.)
  if (!props.id) {
    // Selection column: fade when hidden in edit mode
    if (isEditing && props.className?.includes('ant-table-selection-column')) {
      const isHidden = hiddenColumns?.includes('__selection__');
      const selectionBodyCellStyle = {
        ...props.style,
        ...(isHidden ? { opacity: HIDDEN_COLUMN_OPACITY } : {}),
      };
      return (
        <td
          {...props}
          style={selectionBodyCellStyle}
          onMouseEnter={() => setHoveredColumnId?.('__selection__')}
          onMouseLeave={() => setHoveredColumnId?.(null)}
        />
      );
    }
    return <td {...props} />;
  }

  const style = {
    ...props.style,
    ...getDragActiveStyle(dragState, props.id, isEditing),
    // Apply ghost effect for hidden columns in edit mode
    ...(isEditing && isColumnHidden ? { opacity: HIDDEN_COLUMN_OPACITY } : {}),
  };

  // When in edit mode, track column hover for showing visibility button in header
  if (isEditing) {
    return (
      <td
        {...props}
        style={style}
        onMouseEnter={() => setHoveredColumnId?.(props.id)}
        onMouseLeave={() => setHoveredColumnId?.(null)}
      />
    );
  }

  return <td {...props} style={style} />;
};

/**
 * Table Features component - renders a row with edit switch on left and folder/pagination on right
 */
const TableFeatures = () => {
  const { token } = theme.useToken();
  const {
    isEditingTable,
    setIsEditingTable,
    query,
    summary,
    resetTableSettings,
    cancelTableEditing,
    hasTableChanges,
    textTransform,
    setTextTransform,
  } = useContext(ListContext);
  const folder = query?.drawer?.folder || 'inbox';
  const paginationSummary = summary?.pagination;

  // Folder icon and label mapping
  const folderConfig = {
    inbox: { icon: <FolderOpenOutlined className="mr-2" />, label: 'documents' },
    documents: { icon: <FolderOpenOutlined className="mr-2" />, label: 'documents' },
    recycle: { icon: <DeleteOutlined className="mr-2" />, label: 'recycle' },
    archive: { icon: <FolderOutlined className="mr-2" />, label: 'archive' },
    filed: { icon: <FolderOutlined className="mr-2" />, label: 'archive' },
  };

  const currentFolder = folderConfig[folder] || folderConfig.inbox;

  const customizeTableStyle = {
    fontSize: 13,
    color: isEditingTable ? token.colorPrimary : token.colorTextSecondary,
  };

  // Build table options menu items
  const tableOptionsMenuItems = [
    {
      key: 'uppercase',
      icon:
        textTransform === 'uppercase' ? (
          <CheckOutlined style={{ color: token.colorPrimary }} />
        ) : (
          <FontSizeOutlined style={{ opacity: 0.3 }} />
        ),
      label: (
        <Tooltip title={translateX('table_option_uppercase_tooltip')} placement="right">
          <span>{translateX('table_option_uppercase')}</span>
        </Tooltip>
      ),
      onClick: () => setTextTransform?.('uppercase'),
    },
    {
      key: 'capitalize',
      icon:
        textTransform === 'capitalize' ? (
          <CheckOutlined style={{ color: token.colorPrimary }} />
        ) : (
          <FontSizeOutlined style={{ opacity: 0.3 }} />
        ),
      label: (
        <Tooltip title={translateX('table_option_capitalize_tooltip')} placement="right">
          <span>{translateX('table_option_capitalize')}</span>
        </Tooltip>
      ),
      onClick: () => setTextTransform?.('capitalize'),
    },
    { type: 'divider' },
    {
      key: 'cancel',
      icon: <UndoOutlined />,
      label: (
        <Tooltip title={translateX('table_option_cancel_tooltip')} placement="right">
          <span>{translateX('cancel')}</span>
        </Tooltip>
      ),
      onClick: cancelTableEditing,
      disabled: !hasTableChanges,
    },
    {
      key: 'reset',
      icon: <ReloadOutlined />,
      label: (
        <Tooltip title={translateX('table_option_reset_tooltip')} placement="right">
          <span>{translateX('reset')}</span>
        </Tooltip>
      ),
      onClick: resetTableSettings,
    },
  ];

  return (
    <Row justify="space-between" align="middle" style={TABLE_FEATURES_ROW_STYLE}>
      {/* Left side: Edit switch and options button */}
      <Col style={{ whiteSpace: 'nowrap' }}>
        <Space>
          <Switch checked={isEditingTable} onChange={setIsEditingTable} size="small" />
          <span style={customizeTableStyle}>{translateX('customize_table')}</span>

          {/* Options button with dropdown menu - only visible when editing */}
          {isEditingTable && (
            <Dropdown
              menu={{ items: tableOptionsMenuItems }}
              trigger={['hover']}
              placement="bottomLeft"
            >
              <Tooltip title={translateX('table_options')}>
                <Button
                  type="text"
                  size="small"
                  icon={<SettingOutlined />}
                  style={{ color: token.colorTextSecondary }}
                />
              </Tooltip>
            </Dropdown>
          )}
        </Space>
      </Col>

      {/* Right side: Folder and pagination info */}
      <Col>
        <Space size="middle">
          {/* Folder indicator */}
          <Tooltip placement="left" title={translateX(currentFolder.label)}>
            <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>
              {currentFolder.icon}
              {translateX(currentFolder.label)}
            </span>
          </Tooltip>

          {/* Pagination info */}
          {paginationSummary && (
            <Tooltip placement="left" title={translateX('viewing_documents')}>
              <span style={{ color: token.colorTextSecondary, fontSize: 13, whiteSpace: 'nowrap' }}>
                {paginationSummary.pages > 1 && (
                  <>
                    <NumericFormat
                      value={
                        paginationSummary.recordsOfFrom ||
                        (paginationSummary.page - 1) * paginationSummary.size + 1
                      }
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="text"
                    />
                    &nbsp;-&nbsp;
                    <NumericFormat
                      value={
                        paginationSummary.recordsOfTo ||
                        Math.min(
                          paginationSummary.page * paginationSummary.size,
                          paginationSummary.total
                        )
                      }
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="text"
                    />
                    &nbsp;{translateX('of')}&nbsp;
                  </>
                )}
                <NumericFormat
                  value={paginationSummary.total || paginationSummary.records || 0}
                  thousandSeparator="."
                  decimalSeparator=","
                  displayType="text"
                />
              </span>
            </Tooltip>
          )}
        </Space>
      </Col>
    </Row>
  );
};

// *******************************************************************************
// HELPERS - Sort Normalization
// *******************************************************************************

/**
 * Normalizes sort configuration to always be an array with explicit index
 * Accepts: null, undefined, object { field, order }, or array [{ field, order, index? }, ...]
 *
 * Sort structure convention:
 * - field: string - Field path to sort by (e.g., 'name', 'cnpj.value', '_metadata.audit.createdAt')
 * - order: 'asc' | 'desc' - Sort direction
 * - index: number - Explicit sort index/priority (1 = highest priority, sorted first)
 *
 */
function normalizeSort(sort) {
  if (!sort) return [];

  let sortArray = [];
  if (Array.isArray(sort)) {
    sortArray = sort.filter(s => s?.field);
  } else if (typeof sort === 'object' && sort.field) {
    sortArray = [sort];
  }

  // Ensure each entry has an index field
  // If index is missing, use array position + 1
  return sortArray
    .map((s, i) => ({
      field: s.field,
      order: s.order || 'asc',
      index: s.index ?? i + 1,
    }))
    .sort((a, b) => a.index - b.index); // Sort by index
}

/**
 * Finds sort config for a specific field
 */
function findSortForField(sortArray, fieldName) {
  return sortArray.find(s => s.field === fieldName) || null;
}

// *******************************************************************************
// GLOBAL TRACKERS (survive StrictMode unmount/remount)
// *******************************************************************************
const settingsInitializedTracker = new Set();

// *******************************************************************************
// CONTEXT
// *******************************************************************************
const ListContext = createContext({});
export const useListContext = () => useContext(ListContext);

// *******************************************************************************
// PROVIDER
// *******************************************************************************
const ListProvider = ({ children, tabName: propTabName }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');
  const storedCompany = useZustand('auth', 'company');
  const subscriptionRef = useRef(null);
  subscriptionRef.current = storedSubscription;
  const companyRef = useRef(null);
  companyRef.current = storedCompany;
  const storedConfig = useZustand('module', 'config');
  const setNavigationHistoryStore = useZustand('module', 'setNavigationHistory');
  const storedNavigationHistory = useZustand('module', 'navigationHistory');
  const storedParameters = useZustand('app', 'parameters');

  const { tab: selectedTab } = useDocumentContext() || {};

  const {
    currentPage: storedCurrentPage,
    listSettings: storedListSettings,
    setListSettings: setListSettingsStore,
  } = useModuleStore();

  // Get table actions from page table config (for buildActionsButtonColumn)
  const tableActions = storedCurrentPage?.table?.actions || [];

  const {
    registerProviderContext,
    listHasNumberOfLineColumns,
    setListHasNumberOfLineColumns,
    handleEditDocument,
    handlePreviewDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
  } = useModuleContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const dragOverlayStyle = {
    backgroundColor: token.colorFillAlter,
    padding: '12px 8px',
    border: `1px solid ${token.colorBorderSecondary}`,
    fontWeight: 500,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    borderRadius: 4,
    whiteSpace: 'nowrap',
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitializedRef = useRef(false);
  const previousQueryRef = useRef(null);
  const hasRefreshedRef = useRef(false);
  const lastSavedPageSizeRef = useRef(null); // Track last saved pageSize to avoid duplicate saves
  const refreshRef = useRef(null); // Ref to store latest refresh function (used by effects defined before refresh)
  const previousCompanyIdRef = useRef(null); // Track previous company to detect company changes
  const contentWrapperRef = useRef(null); // Ref for the content div — used by the wheel scroll fix

  // Initialize API and get both functions and state values
  // useApiInitializer returns: { get, post, put, del, done, data, loading, setLoading, error, summary, urlInfo, ... }
  const api = useApiInitializer();
  const { get, data, loading, setLoading, summary, urlInfo } = api || {};

  // Use useApi for side effects (autoFetch handling, transition management),
  // but we don't need its return value since useApiInitializer already has everything
  useApi({ hideLoading: false });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const tabName = propTabName || selectedTab;
  const moduleName = storedConfig?.id;
  const pageName = storedCurrentPage?.name;
  const historyKey = buildHistoryKey(moduleName, pageName, tabName);

  // Get bootstrap config from page pagination settings (same structure as query state)
  const tabConfig = storedCurrentPage?.tabs?.[tabName];
  const bootstrapQuery = tabConfig?.pagination || storedCurrentPage?.pagination;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [query, setQueryState] = useState(() => {
    // Priority: URL > navigationHistory > bootstrap > default
    const urlQuery = decodeQuery(searchParams.get(QUERY_PARAM_KEY));
    const historyQuery = storedNavigationHistory?.[historyKey];
    return mergeWithBootstrap(bootstrapQuery, urlQuery || historyQuery);
  });

  const [tableKey, setTableKey] = useState(0);
  // NOTE: pagination is now computed via useMemo (see section 9) instead of useState + useEffect
  // This ensures synchronous updates and prevents Ant Design Table warnings during transitions
  const [availableTableColumns, setAvailableTableColumns] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]); // Track expanded subtable rows
  const [floatButtonCustomActionsFn, setFloatButtonCustomActionsFn] = useState(null);
  const [floatButtonDisabledActionsFn, setFloatButtonDisabledActionsFn] = useState(null);
  const bootstrapAppliedRef = useRef(false);

  // Drag and drop state for column reordering
  const [dragIndex, setDragIndex] = useState({ active: -1, over: -1 });
  const [columnOrder, setColumnOrder] = useState(null); // null = use original order
  const columnOrderInitializedRef = useRef(false);

  // Table edit mode state
  const [isEditingTable, setIsEditingTable] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState([]); // Array of column keys to hide
  const [editStartSnapshot, setEditStartSnapshot] = useState(null); // Snapshot of state when edit mode started
  const [hoveredColumnId, setHoveredColumnId] = useState(null); // Track which column is being hovered (for visibility button)
  const [textTransform, setTextTransform] = useState('uppercase'); // 'uppercase' or 'capitalize' for table data display

  // Header text transform - hardcoded for consistency across all tables
  // Options: 'uppercase', 'capitalize', 'none'
  // const headerTextTransform = 'capitalize';
  const headerTextTransform = 'uppercase';

  // dnd-kit sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Minimum distance before drag starts (prevents accidental drags)
      },
    })
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // Register listContext flag in ModuleProvider (handled by registerProviderContext below)
  // This effect is now deprecated - the context flag is included in the main registration

  // Apply bootstrap config when it becomes available (handles timing issue with Zustand store)
  useEffect(() => {
    if (bootstrapAppliedRef.current || !bootstrapQuery) return;
    bootstrapAppliedRef.current = true;

    // Re-merge with bootstrap config now that it's available
    setQueryState(prev => mergeWithBootstrap(bootstrapQuery, prev));
  }, [bootstrapQuery]);

  // Load table preferences (column order, hidden columns, and pageSize) from module preferences
  // Priority: backend preferences > localStorage > config defaults
  // Get module-specific table preferences from backend parameters
  const moduleTablePrefs = storedParameters?.modules?.[storedConfig?.id]?.table?.[pageName];

  useEffect(() => {
    if (!pageName) return;

    // Wait for parameters to be loaded before initializing
    // If parameters are null, the backend hasn't loaded yet
    if (storedParameters === null) return;

    // Skip if already initialized with the same preferences
    // This prevents unnecessary re-renders while allowing updates when preferences change
    if (columnOrderInitializedRef.current) return;
    columnOrderInitializedRef.current = true;

    // Get default hidden columns from page config (table.hiddenColumns)
    const defaultHiddenColumns = storedCurrentPage?.table?.hiddenColumns || [];

    // Try to load from module preferences first (backend persisted)
    const tablePrefs = appService.getModulePreferences({
      category: 'table',
      key: pageName,
    });

    if (tablePrefs) {
      // Load from module preferences (user has saved preferences)
      if (Array.isArray(tablePrefs.columnOrder) && tablePrefs.columnOrder.length > 0) {
        setColumnOrder(tablePrefs.columnOrder);
      }
      // hiddenColumns can be empty array (user chose to show all) - check if key exists
      if ('hiddenColumns' in tablePrefs) {
        setHiddenColumns(tablePrefs.hiddenColumns || []);
      } else if (defaultHiddenColumns.length > 0) {
        // No saved hiddenColumns, apply config defaults
        setHiddenColumns(defaultHiddenColumns);
      }
      // Load textTransform preference (default: uppercase)
      if (tablePrefs.textTransform) {
        setTextTransform(tablePrefs.textTransform);
      }
      // Load pageSize from backend preferences and apply to query state
      if (tablePrefs.pageSize && tablePrefs.pageSize > 0) {
        lastSavedPageSizeRef.current = tablePrefs.pageSize; // Initialize ref to avoid duplicate save
        setQueryState(prev => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            size: tablePrefs.pageSize,
          },
        }));
      }
    } else {
      // No backend preferences - check localStorage
      const savedOrder = getStorageModuleItem(`[${pageName}].columnOrder`);
      if (Array.isArray(savedOrder) && savedOrder.length > 0) {
        setColumnOrder(savedOrder);
      }

      // Restore hidden columns from localStorage (saved when user exits edit mode)
      const savedHidden = getStorageModuleItem(`[${pageName}].hiddenColumns`);
      if (Array.isArray(savedHidden)) {
        // Use localStorage value even if empty array (means user chose to show all columns)
        setHiddenColumns(savedHidden);
      } else if (defaultHiddenColumns.length > 0) {
        // No saved preferences at all - apply config defaults for hidden columns
        setHiddenColumns(defaultHiddenColumns);
      }

      // Check localStorage for pageSize (fallback for backwards compatibility)
      const savedPageSize = getStorageModuleItem(`[${pageName}].pageSize`);
      if (savedPageSize && savedPageSize > 0) {
        lastSavedPageSizeRef.current = savedPageSize; // Initialize ref to avoid duplicate save
        setQueryState(prev => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            size: savedPageSize,
          },
        }));
      }
    }

    // Cleanup tracker on unmount
    return () => {
      setTimeout(() => {
        columnOrderInitializedRef.current = false;
      }, 1000);
    };
  }, [pageName, storedCurrentPage?.table?.hiddenColumns, storedParameters]);

  // Re-sync hidden columns when module preferences change from backend (e.g., after saving in modal)
  useEffect(() => {
    // Only update if we've already initialized and have valid preferences
    if (!columnOrderInitializedRef.current || !moduleTablePrefs) return;

    // Update hidden columns if they changed
    if ('hiddenColumns' in moduleTablePrefs) {
      setHiddenColumns(moduleTablePrefs.hiddenColumns || []);
    }

    // Update column order if it changed
    if (Array.isArray(moduleTablePrefs.columnOrder) && moduleTablePrefs.columnOrder.length > 0) {
      setColumnOrder(moduleTablePrefs.columnOrder);
    }

    // Update textTransform if it changed
    if (moduleTablePrefs.textTransform) {
      setTextTransform(moduleTablePrefs.textTransform);
    }
  }, [moduleTablePrefs]);

  // Sync query to URL and navigation history
  useEffect(() => {
    const isTabSelected = !tabName || selectedTab === tabName;

    if (!isTabSelected) {
      // Tab is not selected - remove query from URL if present
      const currentEncoded = searchParams.get(QUERY_PARAM_KEY);
      if (currentEncoded) {
        setSearchParams(
          prev => {
            prev.delete(QUERY_PARAM_KEY);
            return prev;
          },
          { replace: true }
        );
      }
      return;
    }

    // Tab is selected - sync query to URL
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      const encoded = encodeQuery(query);
      const currentEncoded = searchParams.get(QUERY_PARAM_KEY);
      if (encoded && encoded !== currentEncoded) {
        setSearchParams(
          prev => {
            prev.set(QUERY_PARAM_KEY, encoded);
            return prev;
          },
          { replace: true }
        );
      }
      return;
    }

    const encoded = encodeQuery(query);
    const currentEncoded = searchParams.get(QUERY_PARAM_KEY);

    // Only update if query actually changed
    if (encoded !== currentEncoded) {
      setSearchParams(
        prev => {
          if (encoded) {
            prev.set(QUERY_PARAM_KEY, encoded);
          } else {
            prev.delete(QUERY_PARAM_KEY);
          }
          return prev;
        },
        { replace: true }
      );

      // Save to navigation history
      if (historyKey && setNavigationHistoryStore) {
        setNavigationHistoryStore(historyKey, query);
      }
    }

    previousQueryRef.current = query;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, historyKey, selectedTab, tabName]);

  // NOTE: pagination is now computed via useMemo in section 9 (COMPUTED VALUES)
  // This ensures synchronous updates and prevents Ant Design Table warnings during transitions

  // Sync filterDrawer from summary (only if changed)
  useEffect(() => {
    const filterDrawer = storedCurrentPage?.summary?.request?.filterDrawer;
    const folderFromQuery = filterDrawer?.folder;
    if (!folderFromQuery) return;

    // Only update if folder actually changed
    if (storedListSettings?.filterDrawer?.folder !== folderFromQuery) {
      setListSettingsStore({ filterDrawer });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedCurrentPage?.summary?.request?.filterDrawer?.folder]);

  // NOTE: The main refresh effect is defined in section 7.1 (after the refresh function)
  // This ensures the effect has access to the refresh function without hoisting issues.

  // Load showNumberOfLine setting (only once per page)
  useEffect(() => {
    const pageName = storedCurrentPage?.name;
    const trackerKey = `showNumberOfLine-${pageName}`;

    if (!storedSubscription?._id || !pageName) return;

    // Use global tracker to survive StrictMode unmount/remount
    if (settingsInitializedTracker.has(trackerKey)) {
      return;
    }
    settingsInitializedTracker.add(trackerKey);

    const showNumberOfLine =
      getStorageModuleItem(`[${pageName}].showNumberOfLine`) === true || false;

    // Only update if value differs from current
    if (storedListSettings?.showNumberOfLine !== showNumberOfLine) {
      setListSettingsStore({ showNumberOfLine });
    }

    // Cleanup tracker on unmount (delayed to survive StrictMode)
    return () => {
      setTimeout(() => {
        settingsInitializedTracker.delete(trackerKey);
      }, 1000);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedSubscription?._id, storedCurrentPage?.name]);

  // Reset selected rows when data changes
  useEffect(() => {
    // Only clear if there are rows selected (prevents unnecessary re-renders)
    setSelectedRows(prev => (prev.length > 0 ? [] : prev));
  }, [data]);

  // Handle edit mode transitions: capture snapshot on enter, save on exit
  const prevIsEditingRef = useRef(isEditingTable);
  useEffect(() => {
    const wasEditing = prevIsEditingRef.current;
    prevIsEditingRef.current = isEditingTable;

    // Capture snapshot when entering edit mode (false -> true)
    if (!wasEditing && isEditingTable) {
      setEditStartSnapshot({
        columnOrder: columnOrder || [],
        hiddenColumns: hiddenColumns || [],
        textTransform: textTransform || 'uppercase',
      });
    }

    // Save and clear snapshot when exiting edit mode (true -> false)
    if (wasEditing && !isEditingTable) {
      // If snapshot is null, it means cancel was clicked (cancelTableEditing clears it first)
      // In this case, skip saving since state was restored to original
      if (!editStartSnapshot) {
        return;
      }

      // Check if there were changes before clearing the snapshot
      const currentOrder = columnOrder || [];
      const snapshotOrder = editStartSnapshot.columnOrder || [];
      const orderChanged =
        currentOrder.length !== snapshotOrder.length ||
        currentOrder.some((col, idx) => col !== snapshotOrder[idx]);

      const currentHidden = [...(hiddenColumns || [])].sort();
      const snapshotHidden = [...(editStartSnapshot.hiddenColumns || [])].sort();
      const hiddenChanged =
        currentHidden.length !== snapshotHidden.length ||
        currentHidden.some((col, idx) => col !== snapshotHidden[idx]);

      const textTransformChanged = textTransform !== editStartSnapshot.textTransform;

      const hadChanges = orderChanged || hiddenChanged || textTransformChanged;

      // Clear the snapshot
      setEditStartSnapshot(null);

      if (pageName && hadChanges) {
        (async () => {
          // Get current preferences to merge with (preserve pageSize)
          const currentPrefs =
            appService.getModulePreferences({
              category: 'table',
              key: pageName,
            }) || {};

          // Save to module preferences (backend)
          const msg = await appService.message({ flag: 'l', text: 'preferences_saving', delay: 0 });
          try {
            await appService.updateModulePreferences({
              category: 'table',
              data: {
                [pageName]: {
                  ...currentPrefs,
                  columnOrder: columnOrder || [],
                  hiddenColumns: hiddenColumns || [],
                  textTransform: textTransform || 'uppercase',
                },
              },
              ignoreSubscription: true,
            });
            msg.update({ flag: 's', text: 'preferences_saving_success', delay: 3 });
          } catch (err) {
            msg.update({ flag: 'e', text: 'preferences_saving_error', delay: 3 });
            console.error('Failed to save table preferences:', err);
          }

          // Also save to localStorage for faster loading on next visit
          setStorageModuleItem(`[${pageName}].columnOrder`, columnOrder);
          setStorageModuleItem(`[${pageName}].hiddenColumns`, hiddenColumns || []);
        })();
      }
    }
  }, [isEditingTable, pageName, columnOrder, hiddenColumns, textTransform, editStartSnapshot]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS - Query Setters (from V2)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Updates query state
   */
  const setQuery = useCallback(update => {
    setQueryState(prev => {
      const next = typeof update === 'function' ? update(prev) : { ...prev, ...update };
      return next;
    });
  }, []);

  /**
   * Sets pagination
   */
  const setPagination = useCallback(
    (page, size) => {
      setQuery(prev => ({
        ...prev,
        pagination: {
          page: page ?? prev.pagination?.page ?? 1,
          size: size ?? prev.pagination?.size ?? 10,
        },
      }));
    },
    [setQuery]
  );

  /**
   * Sets sort configuration (supports single field or multi-field)
   *
   * Usage:
   * - Single field: setSort('name', 'asc')
   * - Replace all: setSort([{ field: 'name', order: 'asc' }, { field: 'cnpj.value', order: 'desc' }])
   * - Add/update field: setSort('email', 'asc', { append: true })
   * - Remove field: setSort('name', null)
   * - Clear all: setSort(null)
   *
   */
  const setSort = useCallback(
    (fieldOrArray, order, options = {}) => {
      setQuery(prev => {
        let newSort;

        // Clear all sorting
        if (fieldOrArray === null || fieldOrArray === undefined) {
          newSort = [];
        }
        // Replace with array
        else if (Array.isArray(fieldOrArray)) {
          newSort = normalizeSort(fieldOrArray);
        }
        // Single field handling
        else if (typeof fieldOrArray === 'string') {
          const field = fieldOrArray;
          const currentSort = normalizeSort(prev.sort);

          // Remove field from sort
          if (order === null) {
            newSort = currentSort
              .filter(s => s.field !== field)
              .map((s, i) => ({ ...s, index: i + 1 })); // Reindex
          }
          // Append mode: add or update field in existing sort
          else if (options.append) {
            const existingIndex = currentSort.findIndex(s => s.field === field);
            if (existingIndex >= 0) {
              // Update existing
              newSort = currentSort.map(s =>
                s.field === field ? { ...s, order: order || 'asc' } : s
              );
            } else {
              // Append new
              const nextIndex =
                currentSort.length > 0 ? Math.max(...currentSort.map(s => s.index)) + 1 : 1;
              newSort = [...currentSort, { field, order: order || 'asc', index: nextIndex }];
            }
          }
          // Replace mode: single field replaces all
          else {
            newSort = [{ field, order: order || 'asc', index: 1 }];
          }
        } else {
          newSort = [];
        }

        return {
          ...prev,
          sort: newSort,
          pagination: { ...prev.pagination, page: 1 },
        };
      });
    },
    [setQuery]
  );

  /**
   * Sets field-specific search
   */
  const setSearch = useCallback(
    (field, value) => {
      setQuery(prev => {
        const search = { ...prev.search };
        if (value) {
          search[field] = value;
        } else {
          delete search[field];
        }
        return {
          ...prev,
          search,
          pagination: { ...prev.pagination, page: 1 },
        };
      });
    },
    [setQuery]
  );

  /**
   * Sets universal text search
   */
  const setTextSearch = useCallback(
    value => {
      setQuery(prev => ({
        ...prev,
        textSearch: value || '',
        pagination: { ...prev.pagination, page: 1 },
      }));
    },
    [setQuery]
  );

  /**
   * Sets column filter
   */
  const setFilter = useCallback(
    (field, values) => {
      setQuery(prev => {
        const filter = { ...prev.filter };
        if (values && values.length > 0) {
          filter[field] = values;
        } else {
          delete filter[field];
        }
        return {
          ...prev,
          filter,
          pagination: { ...prev.pagination, page: 1 },
        };
      });
    },
    [setQuery]
  );

  /**
   * Sets drawer filter values
   */
  const setDrawerFilter = useCallback(
    values => {
      setQuery(prev => ({
        ...prev,
        drawer: { ...prev.drawer, ...values },
        pagination: { ...prev.pagination, page: 1 },
      }));
      setListSettingsStore?.({ filterDrawer: values });
    },
    [setQuery, setListSettingsStore]
  );

  /**
   * Clears all filters and searches, resetting to module's initial configuration
   * Keeps current pagination size and drawer folder
   */
  const clearFilters = useCallback(() => {
    // Merge with bootstrap to get module's initial config (including default sort)
    const defaultQuery = mergeWithBootstrap(bootstrapQuery, null);

    setQuery(prev => ({
      ...defaultQuery,
      pagination: { page: 1, size: prev.pagination?.size || defaultQuery.pagination?.size || 10 },
      drawer: {
        ...defaultQuery.drawer,
        folder: prev.drawer?.folder || defaultQuery.drawer?.folder || 'inbox',
      },
    }));
    setTableKey(k => k + 1);
  }, [setQuery, bootstrapQuery]);

  /**
   * Resets query to bootstrap defaults
   */
  const resetToDefaults = useCallback(() => {
    const defaultQuery = mergeWithBootstrap(bootstrapQuery, null);
    setQueryState(defaultQuery);
    setTableKey(k => k + 1);
  }, [bootstrapQuery]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6.5. HANDLERS - Column Drag and Drop
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Handle drag end - reorder columns and persist to localStorage
   */
  const onDragEnd = useCallback(
    ({ active, over }) => {
      if (active.id !== over?.id) {
        setAvailableTableColumns(prevColumns => {
          const activeIndex = prevColumns.findIndex(col => col.key === active?.id);
          const overIndex = prevColumns.findIndex(col => col.key === over?.id);
          const newColumns = arrayMove(prevColumns, activeIndex, overIndex);

          // Save new order to localStorage (only keys)
          const newOrder = newColumns.map(col => col.key);
          setColumnOrder(newOrder);
          setStorageModuleItem(`[${pageName}].columnOrder`, newOrder);

          return newColumns;
        });
      }
      setDragIndex({ active: -1, over: -1 });
    },
    [pageName]
  );

  /**
   * Handle drag over - update visual feedback
   */
  const onDragOver = useCallback(
    ({ active, over }) => {
      const activeIndex = availableTableColumns.findIndex(col => col.key === active.id);
      const overIndex = availableTableColumns.findIndex(col => col.key === over?.id);
      setDragIndex({
        active: active.id,
        over: over?.id,
        direction: overIndex > activeIndex ? 'right' : 'left',
      });
    },
    [availableTableColumns]
  );

  /**
   * Reset column order to original
   */
  const resetColumnOrder = useCallback(() => {
    setColumnOrder(null);
    setStorageModuleItem(`[${pageName}].columnOrder`, null);
    setTableKey(k => k + 1); // Force table re-render
  }, [pageName]);

  /**
   * Toggle column visibility (hide/show)
   */
  const toggleColumnVisibility = useCallback(columnKey => {
    if (!columnKey) return;
    setHiddenColumns(prev => {
      if (prev.includes(columnKey)) {
        return prev.filter(key => key !== columnKey);
      }
      return [...prev, columnKey];
    });
  }, []);

  /**
   * Hide a column by key
   */
  const hideColumn = useCallback(columnKey => {
    if (!columnKey) return;
    setHiddenColumns(prev => {
      if (prev.includes(columnKey)) return prev;
      return [...prev, columnKey];
    });
  }, []);

  /**
   * Show a hidden column by key
   */
  const showColumn = useCallback(columnKey => {
    if (!columnKey) return;
    setHiddenColumns(prev => prev.filter(key => key !== columnKey));
  }, []);

  /**
   * Reset hidden columns to default from config (table.hiddenColumns)
   */
  const resetHiddenColumns = useCallback(() => {
    const defaultHiddenColumns = storedCurrentPage?.table?.hiddenColumns || [];
    setHiddenColumns(defaultHiddenColumns);
  }, [storedCurrentPage?.table?.hiddenColumns]);

  /**
   * Reset table to default state (order + hidden columns + textTransform)
   */
  const resetTableSettings = useCallback(() => {
    resetColumnOrder();
    resetHiddenColumns();
    setTextTransform('uppercase'); // Reset to default
    setIsEditingTable(false);
  }, [resetColumnOrder, resetHiddenColumns]);

  /**
   * Cancel table editing - restore to state before editing started
   */
  const cancelTableEditing = useCallback(() => {
    if (editStartSnapshot) {
      // Restore to the state when edit mode was activated
      setColumnOrder(
        editStartSnapshot.columnOrder?.length > 0 ? editStartSnapshot.columnOrder : null
      );
      setHiddenColumns(editStartSnapshot.hiddenColumns || []);
      setTextTransform(editStartSnapshot.textTransform || 'uppercase');
    }
    setEditStartSnapshot(null);
    setIsEditingTable(false);
  }, [editStartSnapshot]);

  /**
   * Check if table has changes since edit mode started
   */
  const hasTableChanges = useMemo(() => {
    if (!editStartSnapshot || !isEditingTable) return false;

    // Compare column order
    const currentOrder = columnOrder || [];
    const snapshotOrder = editStartSnapshot.columnOrder || [];
    const orderChanged =
      currentOrder.length !== snapshotOrder.length ||
      currentOrder.some((col, idx) => col !== snapshotOrder[idx]);

    // Compare hidden columns
    const currentHidden = [...(hiddenColumns || [])].sort();
    const snapshotHidden = [...(editStartSnapshot.hiddenColumns || [])].sort();
    const hiddenChanged =
      currentHidden.length !== snapshotHidden.length ||
      currentHidden.some((col, idx) => col !== snapshotHidden[idx]);

    // Compare textTransform
    const textTransformChanged = textTransform !== editStartSnapshot.textTransform;

    return orderChanged || hiddenChanged || textTransformChanged;
  }, [columnOrder, hiddenColumns, textTransform, editStartSnapshot, isEditingTable]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. HANDLERS - Table & API
  // ─────────────────────────────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    const layout = storedCurrentPage?.layout;
    const apiUrl = storedCurrentPage?.api?.url;
    const filterByCompany = storedCurrentPage?.settings?.filterByCompany;

    // Only refresh for list pages - prevents calling wrong API during navigation
    if (layout !== 'list') return;

    // Only call API if there's a valid URL configured
    if (apiUrl) {
      // CRITICAL: Read subscription directly from Zustand store (non-reactive) to always get the latest value
      // Skip fetch if subscription is not loaded yet (prevents cross-subscription data leak)
      const fetchWithoutSubscription = storedCurrentPage?.api?.fetchWithoutSubscription;
      if (!fetchWithoutSubscription) {
        const currentSubscription = subscriptionRef.current;
        if (!currentSubscription?._id) return;
      }
      const currentCompany = companyRef.current;

      // Build query object with all pagination state (matches ListProvider state structure)
      // This will be encoded as 'q' parameter and sent to backend
      const queryObject = {
        pagination: query?.pagination || { page: 1, size: 10 },
        sort: query?.sort || [],
        search: query?.search || {},
        textSearch: query?.textSearch || '',
        filter: query?.filter || {},
        drawer: query?.drawer || { folder: 'inbox' },
      };

      // Include companyId inside the encrypted query (not exposed in URL)
      // Use currentCompany (fresh from store) instead of storedCompany (from closure)
      if (filterByCompany && currentCompany?._id) {
        queryObject.companyId = currentCompany._id;
      }

      // Encode query as 'q' parameter (base64 encoded JSON)
      // Backend will decode using httpService.decodePaginationQuery()
      const encodedQ = encodeQuery(queryObject);

      // Send ONLY the 'q' parameter (encrypted) - no individual params exposed
      // showLoading: true (default) sets loading state to true
      // hideLoading: true ensures loading is set to false after completion
      await get?.({ url: apiUrl, query: { q: encodedQ }, hideLoading: true });
    }

    // Clear selected rows
    setSelectedRows(prev => (prev.length > 0 ? [] : prev));
  }, [
    get,
    storedCurrentPage?.api?.url,
    storedCurrentPage?.settings?.filterByCompany,
    storedCurrentPage?.layout,
    // REMOVED: storedCompany?._id - now reading directly from store inside the function
    query,
  ]);

  // Update ref on every render so other parts of the code can access the latest refresh function
  refreshRef.current = refresh;

  // ─────────────────────────────────────────────────────────────────────────────
  // 7.1 EFFECT - Refresh on query/company change
  // ─────────────────────────────────────────────────────────────────────────────
  // Uses refreshRef.current to always call the latest refresh function without
  // including refresh in dependencies (which would cause infinite loops)
  useEffect(() => {
    const apiUrl = storedCurrentPage?.api?.url;
    const filterByCompany = storedCurrentPage?.settings?.filterByCompany;
    const fetchWithoutSubscription = storedCurrentPage?.api?.fetchWithoutSubscription;

    // Wait for storedCurrentPage to be loaded before doing anything
    if (!storedCurrentPage?.name) return;

    // CRITICAL: Wait for subscription to be loaded before fetching
    // This prevents fetching ALL records on page refresh before subscription is available
    // which would result in backend returning records from ALL subscriptions (security issue)
    // EXCEPTION: Pages with fetchWithoutSubscription: true don't need subscription (e.g., account pages)
    if (!fetchWithoutSubscription && !storedSubscription?._id) {
      return; // Will re-execute when storedSubscription becomes available (dependency)
    }

    // CRITICAL: If filterByCompany is enabled, wait for company to be loaded
    // This prevents fetching ALL records on page refresh before company is available (security issue)
    if (filterByCompany && !storedCompany?._id) {
      return; // Will re-execute when storedCompany becomes available (dependency)
    }

    // Detect company change and reset pagination to page 1
    // This prevents issues when switching from company with 160 pages to one with 133 pages
    // while being on page 160 (which doesn't exist in the new company)
    const companyChanged =
      previousCompanyIdRef.current !== null && previousCompanyIdRef.current !== storedCompany?._id;

    // Update previous company ref BEFORE any early returns
    previousCompanyIdRef.current = storedCompany?._id;

    if (companyChanged) {
      const currentPage = query?.pagination?.page || 1;

      if (currentPage !== 1) {
        // Reset to page 1 when company changes and not already on page 1
        setQuery(prev => ({
          ...prev,
          pagination: { ...prev.pagination, page: 1 },
        }));
        // Don't call refresh here - the setQuery above will trigger another effect run
        return;
      }
      // If already on page 1, continue to refresh below
    }

    if (!hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      // On first render, only call refresh if there's an API URL configured
      if (apiUrl) refreshRef.current?.();
      return;
    }

    refreshRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Using JSON.stringify(query) instead of refresh to avoid infinite loops
    // The ref ensures we always call the latest refresh with current query state
    JSON.stringify(query),
    storedCurrentPage?.name,
    storedCurrentPage?.api?.url,
    storedCompany?._id, // When company changes, trigger a refresh
    storedSubscription?._id, // When subscription loads/changes, trigger a refresh
  ]);

  /**
   * Handles Ant Design Table onChange event
   * Supports multi-column sorting when multiple sorters are passed
   */
  const handleTableChanges = useCallback(
    (p, f, s) => {
      // ─────────────────────────────────────────────────────────────────────────
      // ANTD 6 FIX: Close pagination Select dropdown
      // ─────────────────────────────────────────────────────────────────────────
      // Strategy A: Blur active element immediately
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }

      // Strategy B: Simulate click outside to trigger Ant Design's close handler
      setTimeout(() => {
        document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, view: window }));
      }, 0);

      // Strategy C: Find and force close any open Select dropdowns
      setTimeout(() => {
        const openSelects = document.querySelectorAll('.ant-select-open');
        openSelects.forEach(select => {
          select.classList.remove('ant-select-open');
          select.classList.remove('ant-select-focused');
          const input = select.querySelector('input');
          if (input) input.blur();
        });

        const dropdowns = document.querySelectorAll(
          '.ant-select-dropdown:not(.ant-select-dropdown-hidden)'
        );
        dropdowns.forEach(dropdown => {
          dropdown.classList.add('ant-select-dropdown-hidden');
        });
      }, 50);

      // ─────────────────────────────────────────────────────────────────────────
      // CLOSE ALL EXPANDED SUBTABLES
      // ─────────────────────────────────────────────────────────────────────────
      // When table changes (sort, search, pagination), close all expanded rows
      // to prevent subtables from belonging to wrong parent documents
      setExpandedRowKeys([]);

      // ─────────────────────────────────────────────────────────────────────────
      // PROCESS CHANGES IMMEDIATELY (no delay for sort to work correctly)
      // ─────────────────────────────────────────────────────────────────────────
      const newPageSize = p.pageSize || 10;

      // Save pageSize to localStorage (fast local access)
      setStorageModuleItem(`[${storedCurrentPage?.name}].pageSize`, newPageSize);

      // Save pageSize to backend preferences when it changes (persistent across devices/logins)
      if (newPageSize !== lastSavedPageSizeRef.current && storedCurrentPage?.name) {
        lastSavedPageSizeRef.current = newPageSize;

        setTimeout(() => {
          // Get current preferences to merge with (preserve columnOrder, hiddenColumns)
          const currentPrefs =
            appService.getModulePreferences({
              category: 'table',
              key: storedCurrentPage.name,
            }) || {};

          appService.updateModulePreferences({
            category: 'table',
            data: {
              [storedCurrentPage.name]: {
                ...currentPrefs,
                pageSize: newPageSize,
              },
            },
            ignoreSubscription: true,
          });
        }, 100);
      }

      setQuery(prev => {
        const next = { ...prev };

        // Handle pagination
        next.pagination = {
          page: p.current || 1,
          size: newPageSize,
        };

        // Handle sorting - supports both single and multi-column sort
        // IMPORTANT: Preserve previous sort when user only changes pagination/filters
        // Only update sort if sorter data is explicitly provided by Ant Design Table
        let newSort = prev.sort || []; // Default: preserve previous sort

        // Check if sort was explicitly changed (not just pagination/filter change)
        // Ant Design passes sorter object/array when user interacts with sort columns
        const hasSorterData = s !== undefined && s !== null;

        if (hasSorterData) {
          if (Array.isArray(s)) {
            // Multi-column sort from Ant Design Table
            const sortedColumns = s.filter(sorter => sorter.column?.dataIndex && sorter.order);
            if (sortedColumns.length > 0) {
              newSort = sortedColumns.map((sorter, i) => ({
                field: Array.isArray(sorter.column.dataIndex)
                  ? sorter.column.dataIndex.join('.')
                  : sorter.column.dataIndex,
                order: sorter.order === 'ascend' ? 'asc' : 'desc',
                index: i + 1,
              }));
            } else {
              // Array exists but no columns have order - user cleared all sorts
              newSort = [];
            }
          } else if (typeof s === 'object') {
            // Single column sort object
            if (s.order && s.column?.dataIndex) {
              // User set a sort direction
              const field = Array.isArray(s.column.dataIndex)
                ? s.column.dataIndex.join('.')
                : s.column.dataIndex;
              newSort = [{ field, order: s.order === 'ascend' ? 'asc' : 'desc', index: 1 }];
            } else {
              // User clicked to clear sort (order is null/undefined/falsy)
              newSort = [];
            }
          }
        }
        // If s is undefined/null (pagination or filter change only), keep prev.sort

        next.sort = newSort;

        // Handle filters and search
        // IMPORTANT: Preserve previous filters/search when user only changes pagination
        // Only update if filter data is explicitly provided by Ant Design Table
        const hasFilterData = f && Object.keys(f).length > 0;

        if (hasFilterData) {
          // Preserve non-column filter keys from previous state (e.g., entityProfile, entityType
          // from bootstrap config) while processing Ant Design column filter changes
          const antDesignKeys = new Set(Object.keys(f));
          const newFilter = {};

          // Keep bootstrap/config filter keys that are NOT managed by Ant Design columns
          Object.entries(prev.filter || {}).forEach(([key, val]) => {
            if (!antDesignKeys.has(key)) {
              newFilter[key] = val;
            }
          });

          const newSearch = { ...prev.search };

          // Process all filter entries from the table
          Object.entries(f).forEach(([key, values]) => {
            const isVisualFilter = VISUAL_FILTER_FIELDS.has(key);

            // Empty array means user cleared this field
            if (!Array.isArray(values) || values.length === 0) {
              // Remove from search if it was a search field
              if (!isVisualFilter) {
                delete newSearch[key];
              }
              return;
            }

            if (values.length > 1) {
              newFilter[key] = values;
            } else if (values.length === 1) {
              const val = values[0];
              if (isVisualFilter) {
                newFilter[key] = [val];
              } else {
                // Text search field - add/update in search
                newSearch[key] = val;
              }
            }
          });

          next.filter = newFilter;
          next.search = newSearch;
        }
        // If no filter data (pagination only), keep prev.filter and prev.search

        return next;
      });
    },
    [setQuery, storedCurrentPage?.name]
  );

  const buildTableColumns = useCallback(
    tabsArr => {
      // Filter columns marked as hidden in config
      let visibleColumns = tabsArr.filter(x => !x.hide);

      // Add key and drag props to each column
      visibleColumns = visibleColumns.map((column, index) => {
        // Generate unique key based on dataIndex or title or index
        const key =
          column.key ||
          (Array.isArray(column.dataIndex) ? column.dataIndex.join('.') : column.dataIndex) ||
          `col-${index}`;

        return {
          ...column,
          key,
          onHeaderCell: () => ({ id: key }),
          onCell: () => ({ id: key }),
        };
      });

      // Filter out user-hidden columns ONLY when NOT editing
      // When editing, show all columns (hidden ones will have ghost opacity)
      if (hiddenColumns.length > 0 && !isEditingTable) {
        visibleColumns = visibleColumns.filter(col => !hiddenColumns.includes(col.key));
      }

      // Apply saved column order if exists
      if (columnOrder && columnOrder.length > 0) {
        const orderedColumns = [];
        const columnMap = new Map(visibleColumns.map(col => [col.key, col]));

        // First, add columns in saved order
        for (const key of columnOrder) {
          if (columnMap.has(key)) {
            orderedColumns.push(columnMap.get(key));
            columnMap.delete(key);
          }
        }

        // Then, add any new columns that weren't in saved order (at the end)
        for (const col of columnMap.values()) {
          orderedColumns.push(col);
        }

        visibleColumns = orderedColumns;
      }

      setAvailableTableColumns(visibleColumns);
    },
    [columnOrder, hiddenColumns, isEditingTable]
  );

  const buildColumnProps = useCallback(
    (
      fieldName,
      {
        sort = false,
        search = false,
        textSearch = false,
        filter = false,
        filterOptions = [],
        title = fieldName,
        searchPlaceholder = null, // Custom placeholder for search input (translated automatically)
        searchValidate = null, // Function to validate search input: (value) => boolean
        searchDecorator = null, // Function to transform search value: (value) => decoratedValue
      } = {}
    ) => {
      // When in edit mode, disable all column interactions (sort, search, filter)
      // to focus only on column reordering and visibility
      if (isEditingTable) {
        return {};
      }

      const sortArray = normalizeSort(query?.sort);
      const filters = query?.filter || {};
      const searchTerms = query?.search || {};

      // Find sort config for this field
      const fieldSort = findSortForField(sortArray, fieldName);

      const sortProps = sort
        ? {
            sorter: true,
            sortOrder: fieldSort ? (fieldSort.order === 'asc' ? 'ascend' : 'descend') : null,
            sortDirections: ['ascend', 'descend', 'ascend'],
          }
        : {};

      const activeSearchValue = searchTerms[fieldName];

      // Filter dropdown component with validation state
      const SearchFilterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const [hasError, setHasError] = useState(false);

        const handleConfirm = () => {
          const rawValue = selectedKeys[0];

          // If empty, just confirm (clear)
          if (!rawValue) {
            setHasError(false);
            confirm();
            return;
          }

          // Validate if validator is provided
          if (searchValidate && !searchValidate(rawValue)) {
            setHasError(true);
            appService.message({ flag: 'e', text: 'invalid_data' });
            return;
          }

          setHasError(false);

          // Decorate value if decorator is provided
          if (searchDecorator) {
            const decoratedValue = searchDecorator(rawValue);
            setSelectedKeys(decoratedValue ? [decoratedValue] : []);
          }

          confirm();
        };

        return (
          <div style={{ padding: 8 }}>
            <Input
              ref={ref => {
                if (ref) {
                  setTimeout(() => {
                    ref.focus();
                  }, 100);
                }
              }}
              placeholder={
                searchPlaceholder
                  ? translateX(searchPlaceholder)
                  : `${translateX('search')} ${translateX(title)?.toLowerCase()}`
              }
              value={selectedKeys[0]}
              onChange={e => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
                if (hasError) setHasError(false); // Clear error on typing
              }}
              onPressEnter={handleConfirm}
              status={hasError ? 'error' : undefined}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={handleConfirm}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                {translateX('search')}
              </Button>
              <Button
                onClick={() => {
                  setHasError(false);
                  clearFilters();
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                {translateX('clear')}
              </Button>
            </Space>
          </div>
        );
      };

      const searchProps =
        search || textSearch
          ? {
              filteredValue: activeSearchValue ? [activeSearchValue] : null,
              filterDropdown: SearchFilterDropdown,
              filterIcon: filtered => (
                <SearchOutlined
                  style={{ color: filtered ? 'var(--ant-color-primary)' : undefined }}
                />
              ),
            }
          : {};

      const filterProps = filter
        ? {
            // Support both string and object formats for filterOptions
            // String: 'draft' → { text: translateX('draft'), value: 'draft' }
            // Object: { text: 'operation_exit', value: 'exit' } → { text: translateX('operation_exit'), value: 'exit' }
            filters: filterOptions.map(option => {
              const isObject = typeof option === 'object' && option !== null;
              const text = isObject ? option.text : option;
              const value = isObject ? option.value : option;
              return {
                text: translateX(text),
                value,
              };
            }),
            filteredValue: Array.isArray(filters?.[fieldName]) ? filters[fieldName] : null,
            onFilter: (value, record) => {
              const fieldValue = record[fieldName];
              return Array.isArray(fieldValue)
                ? fieldValue.includes(value)
                : String(fieldValue) === String(value);
            },
          }
        : {};

      if (textSearch && !TEXT_SEARCH_FIELDS.includes(fieldName)) {
        TEXT_SEARCH_FIELDS.push(fieldName);
      }

      if (filter && !VISUAL_FILTER_FIELDS.has(fieldName)) {
        VISUAL_FILTER_FIELDS.add(fieldName);
      }

      return {
        ...sortProps,
        ...searchProps,
        ...filterProps,
      };
    },
    [query, isEditingTable]
  );

  const registerFloatButtonCustomActions = useCallback(fn => {
    setFloatButtonCustomActionsFn(() => fn);
  }, []);

  const registerFloatButtonDisabledActions = useCallback(fn => {
    setFloatButtonDisabledActionsFn(() => fn);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7.5. PAGINATION (computed before column builders that depend on it)
  // ─────────────────────────────────────────────────────────────────────────────

  // Compute pagination synchronously (not via effect) to prevent Ant Design warnings during transitions
  // This replaces the previous useState + useEffect approach
  const pagination = useMemo(() => {
    const apiPagination = summary?.pagination;
    const storedPagination = storedCurrentPage?.summary?.pagination;
    const dataLength = data?.length || 0;
    const requestedPageSize = query?.pagination?.size || 10;
    const rawTotal =
      apiPagination?.total || apiPagination?.records || storedPagination?.records || 0;

    // Prevent Ant Design warning during pageSize transitions
    // Warning occurs when: dataLength < total AND dataLength > pageSize
    // Fix: temporarily adjust total to equal dataLength during transitions
    const adjustedTotal =
      dataLength > requestedPageSize && dataLength < rawTotal ? dataLength : rawTotal;

    return {
      size: 'small',
      current:
        apiPagination?.currentPage ||
        apiPagination?.page ||
        storedPagination?.currentPage ||
        query?.pagination?.page ||
        1,
      pageSize: requestedPageSize,
      total: adjustedTotal,
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
    };
  }, [summary, storedCurrentPage?.summary?.pagination, query?.pagination, data?.length]);

  // Convert query to Ant Design Table compatible props
  const tableProps = useMemo(() => {
    // Reuse values from pagination memo for consistency
    return {
      pagination: {
        ...pagination,
      },
    };
  }, [pagination]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. COLUMN BUILDERS
  // ─────────────────────────────────────────────────────────────────────────────
  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedRows.map(row => row._id),
      onChange: (_, selectedRowsNew) => {
        setSelectedRows(selectedRowsNew);
      },
      preserveSelectedRowKeys: false,
    }),
    [selectedRows]
  );

  const buildNumberLineColumn = useCallback(
    ({ title = 'line' } = {}) => {
      // if (!listHasNumberOfLineColumns) {
      //   setTimeout(() => setListHasNumberOfLineColumns?.(true), 0);
      // }

      return {
        key: 'line',
        // hide: !storedListSettings?.showNumberOfLine,
        title: translateX(title),
        width: 60,
        align: 'right',
        render: (_, __, index) => {
          const current = pagination?.current || 1;
          const pageSize = pagination?.pageSize || 10;
          const lineNumber = (current - 1) * pageSize + index + 1;

          return <span className="text-muted">{lineNumber}</span>;
        },
      };
    },
    [
      pagination,
      storedListSettings?.showNumberOfLine,
      listHasNumberOfLineColumns,
      setListHasNumberOfLineColumns,
    ]
  );

  const buildDocumentCodeColumn = useCallback(
    ({ title = 'document', dataIndex = '_metadata.documentCode', width = 140, search = false } = {}) => {
      return {
        key: 'documentCode',
        title: translateX(title),
        dataIndex,
        width,
        className: 'text-nowrap',
        ...buildColumnProps(dataIndex, { sort: true, search, title: 'document' }),
        render: (_, record) => {
          const legacyCode = record?.legacyCode;
          const lcValue = Array.isArray(legacyCode) ? legacyCode.join(',') : legacyCode;

          return (
            <div>
              <Text strong style={{ display: 'block' }} className="text-uppercase">
                {record?._metadata?.__documentCode || '—'}
              </Text>
              {lcValue && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  LC: {lcValue}
                </Text>
              )}
            </div>
          );
        },
      };
    },
    [buildColumnProps]
  );

  const buildCreatedAtColumn = useCallback(
    ({
      key = 'createdAt', // key for hiddenColumns matching
      title = 'created',
      dataIndex = '_metadata.audit.createdAt',
      width = 160,
      columnProps = { sort: true },
    } = {}) => {
      return {
        key,
        title: translateX(title),
        dataIndex,
        width,
        className: 'text-nowrap text-transform-content-none',
        ...buildColumnProps(dataIndex, { title: 'for_period', sort: true, ...columnProps }),
        render: (_, record) => (
          <>
            {record?._metadata?.audit?.createdAt ? (
              <Tooltip title={dayjs(record?._metadata.audit?.createdAt).format('L LTS')}>
                {dayjs(record?._metadata?.audit?.createdAt).fromNow()}
              </Tooltip>
            ) : null}
          </>
        ),
      };
    },
    [buildColumnProps]
  );

  const buildUpdatedAtColumn = useCallback(
    ({
      key = 'updatedAt', // key for hiddenColumns matching
      title = 'updated',
      dataIndex = '_metadata.audit.updatedAt',
      width = 160,
      columnProps = { sort: true },
    } = {}) => {
      return {
        key,
        title: translateX(title),
        dataIndex,
        width,
        className: 'text-nowrap text-transform-content-none',
        ...buildColumnProps(dataIndex, { title: 'for_period', sort: true, ...columnProps }),
        render: (_, record) => (
          <>
            {record?._metadata?.audit?.updatedAt ? (
              <Tooltip title={dayjs(record?._metadata.audit?.updatedAt).format('L LTS')}>
                {dayjs(record?._metadata?.audit?.updatedAt).fromNow()}
              </Tooltip>
            ) : null}
          </>
        ),
      };
    },
    [buildColumnProps]
  );

  const buildEntityProfileColumn = useCallback(
    ({ title = 'profile', dataIndex = 'profile', align = 'center', columnProps = {} } = {}) => {
      return {
        title: translateX(title),
        dataIndex,
        align,
        className: 'text-nowrap',
        ...buildColumnProps(dataIndex, { ...columnProps }),
        render: (_, record) => (
          <SimpleEntityProfileTag tags={record?.entityProfile} orientation="vertical" />
        ),
      };
    },
    [buildColumnProps]
  );

  const buildDocumentInfoIconsColumn = useCallback(
    ({
      key = 'documentInfoIcons',
      title = 'info',
      dataIndex = '_metadata.origin',
      width = 50,
      align = 'center',
      size = 16,
      filter = false,
      filterOptions = null,
    } = {}) => {
      // Map string labels to numeric values for _metadata.origin
      const originValueMap = {
        manual: 0,
        migrated: 1,
        automatic: 2,
        ai: 3,
      };

      // Convert filterOptions to use numeric values but keep string labels for translation
      const mappedFilterOptions = filterOptions?.map(opt => originValueMap[opt] ?? opt) || [];

      const columnDef = {
        key,
        title: translateX(title),
        dataIndex,
        width,
        align,
        render: (_, record) => <DocumentInfoIcons data={record} size={size} />,
      };

      // Add filter if enabled
      if (filter && filterOptions) {
        // Build custom filters with translated text and numeric values
        const filters = filterOptions.map((label, index) => ({
          text: translateX(label),
          value: mappedFilterOptions[index],
        }));

        // Get current filter values from query.filter
        const currentFilterValues = query?.filter?.[dataIndex] || null;

        columnDef.filters = filters;
        columnDef.filteredValue = currentFilterValues;
        columnDef.filterIcon = filtered => (
          <FilterOutlined style={{ color: filtered ? 'var(--ant-color-primary)' : undefined }} />
        );
      }

      return columnDef;
    },
    [query?.filter]
  );

  const buildActionsButtonColumn = useCallback(
    ({
      title = 'actions',
      edit = tableActions?.includes('edit') || false,
      view = tableActions?.includes('view') || false,
      archive = tableActions?.includes('archive') || false,
      remove = tableActions?.includes('remove') || false,
      restore,
      align = 'center',
      width = 80,
      customRender = null,
      fitInDropDownMenu = [],
      // Optional fn(record) => [{ key, icon, label, onClick, disabled?, danger? }]
      // Buttons returned are prepended before the standard action buttons
      prependButtons = null,
    } = {}) => {
      const folder = storedListSettings?.filterDrawer?.folder || query?.drawer?.folder;
      if (['archive', 'recycle'].includes(folder)) {
        restore = true;
        remove = true;
        archive = false;
      }

      // Action options to pass to customRender
      const actionOptions = { view, edit, archive, remove, restore };

      return {
        title: translateX(title),
        align,
        width,
        fixed: 'right',
        render: customRender
          ? (text, record) => customRender(text, record, actionOptions)
          : (_, record) => {
              const buttons = [];
              const isRecycle = folder === 'recycle';

              // Prepend custom per-record buttons before standard actions
              if (prependButtons) {
                const custom = prependButtons(record) || [];
                buttons.push(...custom);
              }

              // Options with onSuccess callback to refresh list after action
              const refreshOptions = { onSuccess: refresh };

              if (view) {
                buttons.push({
                  key: 'view',
                  icon: <EyeOutlined />,
                  label: translateX('view'),
                  onClick: () => handlePreviewDocument?.(record),
                });
              }

              if (edit) {
                buttons.push({
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: translateX('edit'),
                  onClick: () => handleEditDocument?.(record),
                });
              }

              if (archive) {
                buttons.push({
                  key: 'archive',
                  icon: <ContainerOutlined />,
                  label: translateX('archive'),
                  onClick: () => handleArchiveDocuments?.([record], refreshOptions),
                });
              }

              if (restore) {
                buttons.push({
                  key: 'restore',
                  icon: <PiLifebuoy />,
                  label: translateX('restore'),
                  onClick: () => handleRestoreDocuments?.([record], refreshOptions),
                });
              }

              if (remove) {
                buttons.push({
                  key: 'remove',
                  icon: <RestOutlined />,
                  label: isRecycle ? translateX('permanently_remove') : translateX('remove'),
                  onClick: () => handleRemoveDocuments?.([record], refreshOptions),
                  danger: isRecycle,
                });
              }

              return renderActionButtons(buttons, fitInDropDownMenu);
            },
      };
    },
    [
      handleEditDocument,
      handlePreviewDocument,
      handleArchiveDocuments,
      handleRemoveDocuments,
      handleRestoreDocuments,
      tableActions,
      storedListSettings?.filterDrawer?.folder,
      query?.drawer?.folder,
      refresh,
    ]
  );

  // Current folder (supports 'inbox', 'documents', 'archive', 'recycle')
  const folder = storedListSettings?.filterDrawer?.folder || query?.drawer?.folder || 'inbox';

  // Shared render function for archive/recycle folders:
  // shows restore button directly + remaining actions in dropdown
  const renderArchiveRecycleActions = useCallback(
    (_, record) => {
      const currentFolder = storedListSettings?.filterDrawer?.folder || query?.drawer?.folder;
      const isRecycle = currentFolder === 'recycle';
      const actionOptions = { onSuccess: refresh };

      const menuItems = [
        {
          key: 'remove',
          icon: <RestOutlined />,
          label: isRecycle ? translateX('permanently_remove') : translateX('remove'),
          danger: isRecycle,
          onClick: () => handleRemoveDocuments?.([record], actionOptions),
        },
      ];

      return (
        <Space size={4}>
          <Tooltip title={translateX('restore')}>
            <Button
              size="small"
              type="text"
              icon={<PiLifebuoy />}
              onClick={() => handleRestoreDocuments?.([record], actionOptions)}
            />
          </Tooltip>
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Tooltip title={translateX('more')}>
              <Button size="small" type="text" icon={<MoreOutlined />} />
            </Tooltip>
          </Dropdown>
        </Space>
      );
    },
    [
      handleRestoreDocuments,
      handleRemoveDocuments,
      storedListSettings?.filterDrawer?.folder,
      query?.drawer?.folder,
      refresh,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. COMPUTED VALUES (from V2)
  // ─────────────────────────────────────────────────────────────────────────────

  // NOTE: pagination and tableProps useMemos were moved to section 7.5 (before column builders)
  // to avoid "Cannot access before initialization" error

  // Build query string for API requests
  const apiQueryString = useMemo(() => {
    return encodeQuery(query);
  }, [query]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const { search, textSearch, filter } = query;
    return (
      Object.keys(search || {}).length > 0 ||
      (textSearch && textSearch.length > 0) ||
      Object.keys(filter || {}).length > 0
    );
  }, [query]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 10. EXPOSED VALUES
  // ─────────────────────────────────────────────────────────────────────────────
  const exposedValues = useMemo(
    () => ({
      // Context identifier
      listContext: true,
      version: 2,

      // Query state (from V2)
      query,
      setQuery,
      apiQueryString,

      // Individual query setters (from V2)
      setPagination,
      setSort,
      setSearch,
      setTextSearch,
      setFilter,
      setDrawerFilter,
      clearFilters,
      resetToDefaults,

      // Table integration
      tableKey,
      tableProps,
      pagination,
      handleTableChanges,
      rowSelection,
      hasActiveFilters,

      // Selection
      selectedRows,
      setSelectedRows,

      // Expandable rows (subtable control)
      expandedRowKeys,
      setExpandedRowKeys,

      // Columns
      availableTableColumns,
      buildTableColumns,

      // Column drag and drop
      columnDragEnabled: true,
      columnOrder,
      resetColumnOrder,
      dragIndex,
      sensors,
      onDragEnd,
      onDragOver,
      tableComponents: {
        header: { cell: TableHeaderCell },
        body: { cell: TableBodyCell },
      },

      // Table edit mode
      isEditingTable,
      setIsEditingTable,
      hiddenColumns,
      toggleColumnVisibility,
      hideColumn,
      showColumn,
      resetHiddenColumns,
      resetTableSettings,
      cancelTableEditing,
      hasTableChanges,
      textTransform,
      setTextTransform,
      TableFeatures, // Component to render edit switch + folder/pagination

      // Column builders
      buildNumberLineColumn,
      buildDocumentCodeColumn,
      buildCreatedAtColumn,
      buildUpdatedAtColumn,
      buildActionsButtonColumn,
      buildEntityProfileColumn,
      buildDocumentInfoIconsColumn,
      buildColumnProps,

      // Folder and shared archive/recycle actions renderer
      folder,
      renderArchiveRecycleActions,

      // useApiInitializer() hook properties
      // IMPORTANT: Spread api FIRST, then override with our specific properties
      // This prevents api.refresh (which is api.get) from overwriting ListProvider's refresh
      ...api,
      data,
      loading,
      setLoading,
      summary,
      urlInfo,

      // Handlers - these MUST come after ...api to not be overwritten
      refresh, // ListProvider's refresh, NOT api.refresh (which is api.get)
      registerFloatButtonCustomActions,
      floatButtonCustomActionsFn,
      registerFloatButtonDisabledActions,
      floatButtonDisabledActionsFn,
      handleEditDocument,
      handlePreviewDocument,
    }),
    [
      query,
      setQuery,
      apiQueryString,
      setPagination,
      setSort,
      setSearch,
      setTextSearch,
      setFilter,
      setDrawerFilter,
      clearFilters,
      resetToDefaults,
      tableKey,
      tableProps,
      pagination,
      columnOrder,
      resetColumnOrder,
      dragIndex,
      sensors,
      onDragEnd,
      onDragOver,
      isEditingTable,
      hiddenColumns,
      toggleColumnVisibility,
      hideColumn,
      showColumn,
      resetHiddenColumns,
      resetTableSettings,
      cancelTableEditing,
      hasTableChanges,
      textTransform,
      handleTableChanges,
      rowSelection,
      hasActiveFilters,
      selectedRows,
      expandedRowKeys,
      availableTableColumns,
      buildTableColumns,
      buildNumberLineColumn,
      buildDocumentCodeColumn,
      buildCreatedAtColumn,
      buildUpdatedAtColumn,
      buildActionsButtonColumn,
      buildEntityProfileColumn,
      buildDocumentInfoIconsColumn,
      buildColumnProps,
      folder,
      renderArchiveRecycleActions,
      refresh,
      registerFloatButtonCustomActions,
      floatButtonCustomActionsFn,
      registerFloatButtonDisabledActions,
      floatButtonDisabledActionsFn,
      handleEditDocument,
      handlePreviewDocument,
      data,
      loading,
      setLoading,
      summary,
      urlInfo,
      api,
    ]
  );

  // Keep a ref to the latest exposedValues to avoid infinite loops
  const exposedValuesRef = useRef(exposedValues);
  exposedValuesRef.current = exposedValues;

  // Register list context in ModuleProvider
  // IMPORTANT: We intentionally do NOT include exposedValues in dependencies
  // to prevent infinite loops. The registration happens once on mount,
  // and consumers should use useListContext() for reactive updates.
  useEffect(() => {
    registerProviderContext?.('listContext', exposedValuesRef.current);

    return () => {
      registerProviderContext?.('listContext', null);
    };
  }, [registerProviderContext]);

  // NOTE: Removed Zustand sync to avoid potential Redux DevTools serialization issues
  // Use React DevTools or useListContext() hook to inspect context values instead

  // Prevent the AntD table from swallowing vertical wheel events.
  // AntD sets overflow:auto as an inline style on its internal scroll container,
  // so CSS overrides cannot reach it. A non-passive wheel listener on the
  // content wrapper intercepts the event first: if the scroll intent is
  // predominantly vertical it is redirected to window.scrollBy and
  // e.preventDefault() stops the table from consuming it.
  // Horizontal scroll (deltaX > deltaY) is left untouched so the table
  // still scrolls sideways normally when scroll={{ x: 'max-content' }} is set.
  useEffect(() => {
    const el = contentWrapperRef.current;
    if (!el) return;

    function onWheel(e) {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      // Allow scroll inside elements that opt-in with className="allow-inner-scroll"
      if (isInsideAllowedScrollContainer(e.target, el)) return;
      window.scrollBy({ top: e.deltaY, behavior: 'instant' });
      e.preventDefault();
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 11. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // Get column keys for SortableContext
  const columnKeys = useMemo(
    () => availableTableColumns.map(col => col.key),
    [availableTableColumns]
  );

  // Get active column title for DragOverlay
  const activeColumnTitle = useMemo(() => {
    const activeCol = availableTableColumns.find(col => col.key === dragIndex.active);
    return activeCol?.title || '';
  }, [availableTableColumns, dragIndex.active]);

  return (
    <ListContext.Provider value={exposedValues}>
      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext items={columnKeys} strategy={horizontalListSortingStrategy}>
          <DragIndexContext.Provider value={dragIndex}>
            <TableEditContext.Provider
              value={{
                isEditing: isEditingTable,
                hiddenColumns,
                onToggleColumnVisibility: toggleColumnVisibility,
                hoveredColumnId,
                setHoveredColumnId,
                textTransform,
              }}
            >
              <style>
                {isEditingTable
                  ? `.table-edit-mode .ant-table-wrapper { border: 3px dotted ${token.colorBorderSecondary} !important; }`
                  : ''}
                {textTransform === 'capitalize'
                  ? `.table-text-capitalize .ant-table-tbody .ant-table-cell { text-transform: capitalize !important; }`
                  : `.table-text-uppercase .ant-table-tbody .ant-table-cell { text-transform: uppercase !important; }`}
                {`.ant-table-tbody .ant-table-cell.text-transform-none { text-transform: none !important; }`}
                {`.ant-table-tbody .ant-table-cell.text-transform-content-none { text-transform: none !important; }`}
                {`.ant-table-thead .ant-table-cell { text-transform: ${headerTextTransform} !important; }`}
                {`.ant-table-thead .ant-table-cell.text-transform-none { text-transform: none !important; }`}
              </style>
              <div
                ref={contentWrapperRef}
                className={`${isEditingTable ? 'table-edit-mode' : ''} ${textTransform === 'capitalize' ? 'table-text-capitalize' : 'table-text-uppercase'}`}
              >
                {children}

                <BackToHomeLink />
              </div>
            </TableEditContext.Provider>
          </DragIndexContext.Provider>
        </SortableContext>

        <DragOverlay>
          {dragIndex.active !== -1 && <div style={dragOverlayStyle}>{activeColumnTitle}</div>}
        </DragOverlay>
      </DndContext>

      {selectedRows.length > 0 && <ListActionsFloatButton />}
    </ListContext.Provider>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ListProvider;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Encodes query object to URL-safe string
 */
function encodeQuery(query) {
  if (!query || Object.keys(query).length === 0) return '';
  try {
    const json = JSON.stringify(query);
    return encodeURIComponent(btoa(json));
  } catch {
    return '';
  }
}

/**
 * Decodes URL string to query object
 */
function decodeQuery(encoded) {
  if (!encoded) return null;
  try {
    const json = atob(decodeURIComponent(encoded));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Merges bootstrap config with current query
 * Config structure matches query state: { pagination, sort, search, textSearch, filter, drawer }
 * Sort is always normalized to array format: [{ field, order, index }, ...]
 */
function mergeWithBootstrap(bootstrap, current) {
  if (!bootstrap) return current || DEFAULT_QUERY;

  // Determine sort: current > bootstrap > default
  // Normalize to ensure consistent array format with index
  const rawSort = current?.sort?.length ? current.sort : bootstrap.sort;
  const normalizedSort = normalizeSort(rawSort);

  return {
    pagination: { ...DEFAULT_QUERY.pagination, ...bootstrap.pagination, ...current?.pagination },
    sort: normalizedSort,
    search: { ...bootstrap.search, ...current?.search },
    textSearch: current?.textSearch ?? bootstrap.textSearch ?? '',
    filter: { ...bootstrap.filter, ...current?.filter },
    drawer: { ...DEFAULT_QUERY.drawer, ...bootstrap.drawer, ...current?.drawer },
  };
}

/**
 * Builds navigation history key for storage
 */
function buildHistoryKey(moduleName, pageName, tabName) {
  const parts = [moduleName, pageName, tabName].filter(Boolean);
  return `listQuery:${parts.join('.')}`;
}

// Returns true if the event target is inside an element with className="allow-inner-scroll",
// meaning that element manages its own scroll and the wheel event should not be intercepted.
function isInsideAllowedScrollContainer(target, stopAt) {
  let node = target;
  while (node && node !== stopAt) {
    if (node.classList?.contains('allow-inner-scroll')) return true;
    node = node.parentElement;
  }
  return false;
}

/**
 * Renders action buttons with responsive behavior
 * - Desktop: up to 4 visible buttons (if more, show 3 + MORE dropdown)
 * - Mobile: 1 visible if only 1 button, otherwise all in MORE dropdown
 * - MORE button disabled if all menu items are disabled
 */
function renderActionButtons(buttons, fitInDropDownMenu = []) {
  if (!buttons || buttons.length === 0) return null;

  const isMobile = isMobileView();

  // If fitInDropDownMenu is provided, separate buttons
  if (fitInDropDownMenu && fitInDropDownMenu.length > 0) {
    const normalButtons = buttons.filter(btn => !fitInDropDownMenu.includes(btn.key));
    const dropdownButtons = buttons.filter(btn => fitInDropDownMenu.includes(btn.key));

    const allDropdownDisabled = dropdownButtons.every(btn => btn.disabled);
    const dropdownMenuItems = dropdownButtons.map(btn => ({
      key: btn.key,
      icon: btn.icon,
      label: btn.label,
      danger: btn.danger,
      disabled: btn.disabled,
      onClick: btn.onClick,
    }));

    return (
      <Space size={4}>
        {/* Render normal buttons */}
        {normalButtons.map(btn => (
          <Tooltip key={btn.key} title={btn.label}>
            <Button
              size="small"
              type="text"
              icon={btn.icon}
              onClick={btn.onClick}
              disabled={btn.disabled}
              danger={btn.danger}
            />
          </Tooltip>
        ))}

        {/* Render dropdown with specified buttons */}
        {dropdownButtons.length > 0 && (
          <Dropdown
            menu={{ items: dropdownMenuItems }}
            trigger={['click']}
            disabled={allDropdownDisabled}
          >
            <Tooltip title={translateX('more')}>
              <Button
                size="small"
                type="text"
                icon={<MoreOutlined />}
                disabled={allDropdownDisabled}
              />
            </Tooltip>
          </Dropdown>
        )}
      </Space>
    );
  }

  // Original logic when fitInDropDownMenu is empty
  // (isMobile already declared above)

  // Mobile: 1 visible if only 1, otherwise all in MORE
  if (isMobile) {
    if (buttons.length === 1) {
      const btn = buttons[0];
      return (
        <Tooltip title={btn.label}>
          <Button
            size="small"
            type="text"
            icon={btn.icon}
            onClick={btn.onClick}
            disabled={btn.disabled}
            danger={btn.danger}
          />
        </Tooltip>
      );
    }

    // Multiple buttons on mobile: all in MORE dropdown
    const allDisabled = buttons.every(btn => btn.disabled);
    const menuItems = buttons.map(btn => ({
      key: btn.key,
      icon: btn.icon,
      label: btn.label,
      danger: btn.danger,
      disabled: btn.disabled,
      onClick: btn.onClick,
    }));

    return (
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={allDisabled}>
        <Tooltip title={translateX('more')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled={allDisabled} />
        </Tooltip>
      </Dropdown>
    );
  }

  // Desktop: up to 4 visible (if more, show 3 + MORE)
  if (buttons.length <= 4) {
    return (
      <Space size={4}>
        {buttons.map(btn => (
          <Tooltip key={btn.key} title={btn.label}>
            <Button
              size="small"
              type="text"
              icon={btn.icon}
              onClick={btn.onClick}
              disabled={btn.disabled}
              danger={btn.danger}
            />
          </Tooltip>
        ))}
      </Space>
    );
  }

  // More than 4 buttons: show first 3 + MORE
  const visibleButtons = buttons.slice(0, 3);
  const menuButtons = buttons.slice(3);
  const allMenuDisabled = menuButtons.every(btn => btn.disabled);

  const menuItems = menuButtons.map(btn => ({
    key: btn.key,
    icon: btn.icon,
    label: btn.label,
    danger: btn.danger,
    disabled: btn.disabled,
    onClick: btn.onClick,
  }));

  return (
    <Space size={4}>
      {visibleButtons.map(btn => (
        <Tooltip key={btn.key} title={btn.label}>
          <Button
            size="small"
            type="text"
            icon={btn.icon}
            onClick={btn.onClick}
            disabled={btn.disabled}
            danger={btn.danger}
          />
        </Tooltip>
      ))}
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={allMenuDisabled}>
        <Tooltip title={translateX('more')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled={allMenuDisabled} />
        </Tooltip>
      </Dropdown>
    </Space>
  );
}
